export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
  correlationId?: string;
  status: number;
}

const ACCESS_KEY = 'sx.access';
const REFRESH_KEY = 'sx.refresh';

export const tokenStore = {
  get access(): string | null {
    return safeRead(ACCESS_KEY);
  },
  get refresh(): string | null {
    return safeRead(REFRESH_KEY);
  },
  set(access: string, refresh: string): void {
    safeWrite(ACCESS_KEY, access);
    safeWrite(REFRESH_KEY, refresh);
  },
  clear(): void {
    safeRemove(ACCESS_KEY);
    safeRemove(REFRESH_KEY);
  },
};

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeWrite(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode or blocked storage — the session simply will not survive a reload */
  }
}
function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

const BASE = '/api/v1';
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Fetch wrapper.
 *
 * On a 401 it tries exactly one refresh and replays the request. Concurrent
 * 401s share a single refresh, so a page with several requests does not rotate
 * the refresh token repeatedly — which the API would read as token reuse and
 * kill the whole session.
 */
export async function api<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  const access = tokenStore.access;
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const response = await fetch(`${BASE}${path}`, { ...init, headers });

  if (response.status === 401 && retry && tokenStore.refresh) {
    const refreshed = await refreshOnce();
    if (refreshed) return api<T>(path, init, false);
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error: ApiError = {
      code: payload.code ?? 'INTERNAL_ERROR',
      message: payload.message ?? 'Something went wrong. Please try again.',
      details: payload.details,
      correlationId: payload.correlationId,
      status: response.status,
    };
    throw error;
  }
  return payload as T;
}

/**
 * An authenticated fetch that is not JSON.
 *
 * `api()` speaks JSON and nothing else, so every upload and every file
 * download reached for `fetch` directly — and each of them forgot the same
 * thing: an access token lasts fifteen minutes, and a 401 after that is not
 * a failure, it is a refresh waiting to happen. A learner with a tab open
 * over lunch pressed Download and was told the certificate could not be
 * downloaded; a video upload failed partway through for the same reason.
 *
 * The response is returned as-is — callers here need the blob, the headers
 * or the status, not a parsed body.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<Response> {
  const headers = new Headers(init.headers);
  const access = tokenStore.access;
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const response = await fetch(`${BASE}${path}`, { ...init, headers });

  if (response.status === 401 && retry && tokenStore.refresh) {
    const refreshed = await refreshOnce();
    // Bodies here are FormData, Blob or a buffer, all of which survive being
    // sent twice. A streamed body would not, and none is used.
    if (refreshed) return apiFetch(path, init, false);
  }
  return response;
}

/**
 * Exported because the access token expires on its own schedule, and every
 * caller that bypasses `api()` — anything fetching something other than JSON
 * — otherwise reimplements this or, worse, treats a 401 as a failure and
 * quietly does without whatever it was fetching.
 */
export async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: tokenStore.refresh }),
      });
      if (!response.ok) {
        tokenStore.clear();
        return false;
      }
      const tokens = (await response.json()) as { accessToken: string; refreshToken: string };
      tokenStore.set(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Allow a later 401 to trigger a fresh attempt.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();
  return refreshInFlight;
}

/** One server-sent event, already parsed. */
export interface SseEvent {
  event: string;
  data: unknown;
}

/**
 * A POST whose response is a stream of server-sent events.
 *
 * Written rather than using `EventSource`, which cannot send an
 * Authorization header and cannot POST — so it could not carry the access
 * token or the question. This is the same `fetch` the rest of the client
 * uses, reading the body as it arrives.
 *
 * Two things it has to get right. A network chunk is not a frame, so the
 * decoder keeps a buffer across reads; without it a long answer loses
 * characters wherever a chunk happened to land. And a 401 gets the same
 * single refresh-and-replay as every other call, or a session that rotated
 * mid-question would fail instead of continuing.
 */
export async function* apiStream(
  path: string,
  body: unknown,
  signal?: AbortSignal,
  retry = true,
): AsyncGenerator<SseEvent> {
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'text/event-stream' });
  const access = tokenStore.access;
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const response = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    signal,
  });

  if (response.status === 401 && retry && tokenStore.refresh) {
    const refreshed = await refreshOnce();
    if (refreshed) {
      yield* apiStream(path, body, signal, false);
      return;
    }
  }

  // An error is still an error envelope, not a stream: the guards run before
  // the handler, so a refusal never reaches the streaming response.
  if (!response.ok || !response.body) {
    const payload = await response.json().catch(() => ({}));
    const error: ApiError = {
      code: payload.code ?? 'INTERNAL_ERROR',
      message: payload.message ?? 'Something went wrong. Please try again.',
      details: payload.details,
      correlationId: payload.correlationId,
      status: response.status,
    };
    throw error;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      // `stream: true` holds a partial multi-byte character rather than
      // emitting a replacement character for its first half.
      buffer += decoder.decode(value, { stream: true });

      // Frames are separated by a blank line.
      let split = buffer.indexOf('\n\n');
      while (split !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);
        const parsed = parseFrame(frame);
        if (parsed) yield parsed;
        split = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
    await response.body.cancel().catch(() => undefined);
  }
}

function parseFrame(frame: string): SseEvent | null {
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of frame.split('\n')) {
    // Comments keep proxies from timing the connection out and carry nothing.
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
  }

  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) };
  } catch {
    return null;
  }
}
